import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';

import { getMenuPermissions } from 'Helper/getMenuPermissions';
import { useLoader } from 'Store/loader';

import SlotContent from 'View/schedule/component/slot-content';
import HeaderContent from 'View/schedule/component/header-content';
import Card from 'View/schedule/component/card';
import OverlayCard from 'View/schedule/component/overlay-card';
import { syncCalendarWithSelectedDate } from 'View/schedule/helper/syncCalendarWithSelectedDate';
import { useScheduleState } from 'View/schedule/state';

import { eventTimeFormat, slotLabelFormat } from './const';
import styles from './styles.module.css';
import './styles.css';

const Calendar = ({ calendarRef, scheduleData, isFetching }) => {
  const { t } = useTranslation('schedule');
  const { state, updateState, filterState } = useScheduleState();
  const { dateRange, viewType } = filterState;
  const { onShowLoader, onResetLoader } = useLoader();

  const cookies = new Cookies();
  const organization = cookies.get('organization');
  const timezone = organization?.timezone;
  const timezoneLabel = useMemo(() => {
    return {
      'Asia/Jakarta': t('wib'),
      'Asia/Kuala_Lumpur': t('myt'),
    };
  }, [t]);

  const schedulePermission = getMenuPermissions('schedule', 'schedule');

  useEffect(() => {
    if (isFetching) onShowLoader({ variant: 'loader-primary' });
    else onResetLoader();
  }, [isFetching, onShowLoader, onResetLoader]);

  useEffect(() => {
    if (!calendarRef?.current) return;

    requestAnimationFrame(() => syncCalendarWithSelectedDate(calendarRef, dateRange, viewType));
  }, [calendarRef, dateRange, viewType]);

  const handleClickCalendar = (e) => {
    const selectedDate = moment.tz(e.dateStr, timezone);
    const startedAt = selectedDate.toISOString();
    const endedAt = selectedDate.add({ hour: 2 }).toISOString();
    const initialSelectedSchedule = { startedAt, endedAt, selectedDate: selectedDate.toISOString() };
    updateState({
      isOpenFormUpsert: true,
      initialSelectedSchedule,
      isEditFormUpsert: false,
    });
  };

  useEffect(() => {
    setTimeout(() => {
      const thElement = document.querySelector('th.fc-timegrid-axis');
      if (thElement) {
        thElement.innerHTML = `<div class="time-head"> <p>${timezoneLabel[timezone]}</p></div>`;
      }
    }, 100);
  }, [timezoneLabel, timezone]);

  const handleCardClick = (eventInfo) => {
    const extendedProps = eventInfo.event?.extendedProps;
    updateState({
      selectedEvent: extendedProps,
      isDrawerOpen: true,
      isEnableFetchDetail: true,
    });
  };

  const handleCloseOverlay = () => {
    updateState({ isDrawerOpen: false, selectedEvent: null, clickPosition: null });
  };

  return (
    <Box className={`calendar__container ${styles['calendar__container']}`}>
      <MemoizedCalendar
        events={scheduleData?.schedules}
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView={'timeGridFourDay'}
        initialDate={dateRange?.startDate}
        views={{ timeGridFourDay: { type: 'timeGrid', duration: { days: viewType.value } } }}
        allDaySlot={false}
        weekends={true}
        headerToolbar={false}
        slotEventOverlap={false}
        dayHeaderContent={(date) => <HeaderContent date={date} />}
        slotLabelContent={(time) => <SlotContent time={time} />}
        eventContent={(eventInfo) => <Card info={eventInfo} onCardClick={(e) => handleCardClick(eventInfo, e)} />}
        eventTimeFormat={eventTimeFormat}
        slotLabelFormat={slotLabelFormat}
        axisFormat="HH:mm"
        locale="en-GB"
        eventOverlap={true}
        height={'calc(100vh - 200px)'}
        dateClick={schedulePermission?.isCanCreate ? handleClickCalendar : undefined}
      />

      <OverlayCard isOpen={state?.isDrawerOpen} onClose={handleCloseOverlay} eventInfo={state?.selectedEvent} clickPosition={state?.clickPosition} />
    </Box>
  );
};

export default Calendar;

const MemoizedCalendar = (props) => {
  const cookies = new Cookies();
  const timezone = cookies.get('organization')?.timezone;

  return useMemo(() => <FullCalendar timeZone={timezone} {...props} />, /* eslint-disable */ [props?.events, props?.views]);
};
